import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";

import { Box, Typography, TextField, Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Episode,
  Paragraph,
  UpdateSubtitlePayload,
} from "../../../services/episode.service";
import VerticalDotsMenu from "./VerticalDotsMenu";
import { episodeService } from "../../../services";
import { toHHMMSS } from "../../../../../utils/formatTIme";

interface Props {
  episode: Episode;
  currentTime: number;
  onParagraphClick: (paragraph: Paragraph) => void;
  onVideoPlay?: () => void;
  onVideoPause?: () => void;
}

// Memoized paragraph component for better performance
const ParagraphItem = React.memo(
  ({
    paragraph,
    index,
    isEditMode,
    currentTime,
    onParagraphClick,
    onTextChange,
    setRef,
    isEditing,
    onStartEdit,
    onStopEdit,
  }: {
    paragraph: Paragraph;
    index: number;
    isEditMode: boolean;
    currentTime: number;
    onParagraphClick: (paragraph: Paragraph) => void;
    onTextChange: (index: number, text: string) => void;
    setRef: (index: number, ref: any) => void;
    isEditing: boolean;
    onStartEdit: (index: number) => void;
    onStopEdit: () => void;
  }) => {
    const isCurrentParagraph = useMemo(
      () => paragraph.start <= currentTime && currentTime <= paragraph.end,
      [paragraph.start, paragraph.end, currentTime],
    );

    const handleClick = useCallback(() => {
      if (isEditMode) {
        onStartEdit(index);
      } else {
        onParagraphClick(paragraph);
      }
    }, [isEditMode, onParagraphClick, paragraph, onStartEdit, index]);

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onTextChange(index, e.target.value);
      },
      [onTextChange, index],
    );

    const handleBlur = useCallback(() => {
      onStopEdit();
    }, [onStopEdit]);

    const setParagraphRef = useCallback(
      (ref: any) => setRef(index, ref),
      [setRef, index],
    );

    // Memoize the background color calculation
    const backgroundColor = useMemo(() => {
      if (isEditMode) {
        // In edit mode, highlight the current paragraph with a subtle background
        return isCurrentParagraph ? "rgba(99, 102, 241, 0.1)" : "transparent";
      }
      // In view mode, highlight the current paragraph with grey background
      return isCurrentParagraph ? "grey.500" : "transparent";
    }, [isEditMode, isCurrentParagraph]);

    // Memoize the text color calculation
    const textColor = useMemo(() => {
      if (isEditMode) {
        // In edit mode, always use black text
        return "#000";
      }
      // In view mode, use white text for highlighted paragraph
      return isCurrentParagraph ? "#fff" : "#000";
    }, [isEditMode, isCurrentParagraph]);

    // Memoize the border radius calculation
    const borderRadius = useMemo(() => {
      if (isEditMode) {
        // In edit mode, use subtle border radius for current paragraph
        return isCurrentParagraph ? "4px" : "0px";
      }
      // In view mode, use rounded corners for highlighted paragraph
      return isCurrentParagraph ? "10px" : "0px";
    }, [isEditMode, isCurrentParagraph]);

    // Memoize the border calculation for edit mode
    const border = useMemo(() => {
      if (isEditMode && isCurrentParagraph) {
        return "1px solid rgba(99, 102, 241, 0.3)";
      }
      return "none";
    }, [isEditMode, isCurrentParagraph]);

    return (
      <Box
        id={`paragraph-${index}`}
        ref={setParagraphRef}
        onClick={handleClick}
        sx={{
          p: 1,
          marginBottom: 2,
          backgroundColor,
          color: textColor,
          borderRadius,
          border,
          minHeight: "60px",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: (theme) =>
              !isEditMode && isCurrentParagraph
                ? "none"
                : `inset 2px 0 0 ${theme.palette.grey[500]}`,
            cursor: "pointer",
          },
        }}
      >
        {isEditMode && isEditing ? (
          <TextField
            fullWidth
            multiline
            variant="standard"
            value={paragraph.text || ""}
            onChange={handleTextChange}
            onBlur={handleBlur}
            autoFocus
            sx={{
              "& .MuiInputBase-root": {
                color: "#000",
                minHeight: "1.5em",
                padding: 0,
                lineHeight: "1.5",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                lineHeight: "1.5",
                padding: 0,
                minHeight: "1.5em",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "pre-wrap",
              },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "1.5",
              minHeight: "1.5em",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {paragraph.text || ""}
          </Typography>
        )}
        <Box display="flex" alignItems="center" mt={1}>
          <Typography variant="caption">
            {toHHMMSS(paragraph.start.toString())}
          </Typography>
          <Typography variant="caption" mx={1}>
            →
          </Typography>
          <Typography variant="caption">
            {toHHMMSS(paragraph.end.toString())}
          </Typography>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if essential props changed
    return (
      prevProps.isEditMode === nextProps.isEditMode &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.paragraph.text === nextProps.paragraph.text &&
      prevProps.paragraph.start === nextProps.paragraph.start &&
      prevProps.paragraph.end === nextProps.paragraph.end &&
      prevProps.currentTime === nextProps.currentTime
    );
  },
);

ParagraphItem.displayName = "ParagraphItem";

export default function SubtitleEditor({
  episode,
  currentTime,
  onParagraphClick,
  onVideoPlay,
  onVideoPause,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const paragraphRefs = useRef<any[]>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isUserScrolling = useRef(false);
  const lastAutoScrollTime = useRef(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedParagraphs, setEditedParagraphs] = useState<Paragraph[]>([]);
  const [editingParagraphIndex, setEditingParagraphIndex] = useState<
    number | null
  >(null);

  // Memoized calculations
  const currentParagraphs = useMemo(
    () =>
      editedParagraphs.length > 0
        ? editedParagraphs
        : (episode?.paragraphs ?? []),
    [editedParagraphs, episode?.paragraphs],
  );

  const currentParagraphIndex = useMemo(
    () =>
      currentParagraphs.findIndex(
        (paragraph) =>
          paragraph.start <= currentTime && currentTime <= paragraph.end,
      ),
    [currentParagraphs, currentTime],
  );

  const setParagraphRef = useCallback((index: number, ref: any) => {
    paragraphRefs.current[index] = ref;
  }, []);

  // Mutations
  const { mutateAsync: exportSubtitle } = useMutation({
    mutationKey: ["exportSubtitle"],
    mutationFn: (format: string) =>
      episodeService.exportSubtitlesByEpisodeId(episode.id, format),
  });

  const { mutateAsync: updateTranscription } = useMutation({
    mutationKey: ["updateTranscription"],
    mutationFn: (payload: UpdateSubtitlePayload) =>
      episodeService.updateTranscription(episode.id, payload),
  });

  // Memoized callbacks
  const handleSaveChanges = useCallback(
    async (paragraph: Paragraph) => {
      try {
        const isSuccess = await updateTranscription({
          paragraph: {
            id: paragraph.id,
            text: paragraph.text,
          },
        });

        if (isSuccess) {
          toast.success("Transcription updated successfully", {
            autoClose: 1000,
          });
        } else {
          toast.error("Failed to update transcription", {
            autoClose: 1000,
          });
        }
      } catch (error) {
        console.error("Error saving changes:", error);
        toast.error("Failed to update transcription", {
          autoClose: 1000,
        });
      }
    },
    [updateTranscription],
  );

  const handleParagraphTextChange = useCallback(
    (index: number, newText: string) => {
      try {
        if (index < 0 || index >= editedParagraphs.length) return;

        const updatedParagraphs = [...editedParagraphs];
        if (updatedParagraphs[index]) {
          updatedParagraphs[index] = {
            ...updatedParagraphs[index],
            text: newText || "",
          };
          setEditedParagraphs(updatedParagraphs);

          // Trigger debounced save with the modified paragraph
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          saveTimeoutRef.current = setTimeout(() => {
            handleSaveChanges(updatedParagraphs[index]);
          }, 800);
        }
      } catch (error) {
        console.error("Error updating paragraph text:", error);
      }
    },
    [editedParagraphs, handleSaveChanges],
  );

  // Memoized handlers
  const handleExportSubtitle = useCallback(
    async (format: string) => {
      try {
        const response: any = await exportSubtitle(format);
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = `episode-${episode?.name}.${format}`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error(`Error exporting ${format}:`, error);
      }
    },
    [exportSubtitle, episode?.name],
  );

  // Clean auto-scroll function
  const scrollToParagraph = useCallback((paragraphIndex: number) => {
    const element = document.getElementById(`paragraph-${paragraphIndex}`);
    const container = containerRef.current;

    if (element && container) {
      const elementTop = element.offsetTop;
      const containerHeight = container.clientHeight;
      const elementHeight = element.clientHeight;

      // Calculate target scroll position to center the element
      const targetScrollTop =
        elementTop - containerHeight / 2 + elementHeight / 2;

      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: "smooth",
      });
    }
  }, []);

  // Debounced scroll function
  const debouncedScrollToParagraph = useCallback(
    (paragraphIndex: number) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollToParagraph(paragraphIndex);
      }, 50); // Reduced from 100ms to 50ms for faster response
    },
    [scrollToParagraph],
  );

  // Clean edit mode entry
  const handleEditSubtitle = useCallback(() => {
    // Initialize editedParagraphs if needed
    if (editedParagraphs.length === 0) {
      setEditedParagraphs([...(episode?.paragraphs ?? [])]);
    }

    // Enter edit mode
    setIsEditMode(true);
    setEditingParagraphIndex(null);

    // Pause video
    if (onVideoPause) {
      onVideoPause();
    }

    // Clear any pending save timeouts
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Scroll to current paragraph after a short delay
    if (currentParagraphIndex !== -1) {
      setTimeout(() => {
        scrollToParagraph(currentParagraphIndex);
      }, 50); // Reduced from 100ms to 50ms for faster response
    }
  }, [
    editedParagraphs.length,
    episode?.paragraphs,
    onVideoPause,
    currentParagraphIndex,
    scrollToParagraph,
  ]);

  // Clean edit mode exit
  const handleCancelEdit = useCallback(() => {
    // Exit edit mode
    setIsEditMode(false);
    setEditingParagraphIndex(null);

    // Clear any pending save timeouts
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Resume video
    if (onVideoPlay) {
      onVideoPlay();
    }

    // Reset auto-scroll timing
    lastAutoScrollTime.current = 0;

    // Scroll to current paragraph
    if (currentParagraphIndex !== -1) {
      setTimeout(() => {
        scrollToParagraph(currentParagraphIndex);
      }, 50); // Reduced from 100ms to 50ms for faster response
    }
  }, [onVideoPlay, currentParagraphIndex, scrollToParagraph]);

  // Effects
  useEffect(() => {
    if (isEditMode) {
      paragraphRefs.current = [];
    }
  }, [isEditMode]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      if (!currentParagraphs.length) return;

      isUserScrolling.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrolling.current = false;
      }, 1500); // Reduced from 2000ms to 1500ms for faster response
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [currentParagraphs.length]);

  useEffect(() => {
    // Initialize editedParagraphs when episode data changes
    if (episode?.paragraphs && episode.paragraphs.length > 0) {
      const needsUpdate =
        editedParagraphs.length === 0 ||
        editedParagraphs.some((p, i) => p.id !== episode.paragraphs?.[i]?.id);

      if (needsUpdate) {
        setEditedParagraphs([...episode.paragraphs]);
      }
    }
  }, [episode?.paragraphs, editedParagraphs.length]);

  // Auto-scroll effect - clean and reliable
  useEffect(() => {
    if (isEditMode || isUserScrolling.current) return;

    const now = Date.now();
    if (now - lastAutoScrollTime.current < 200) {
      // Reduced from 300ms to 200ms for faster response
      return;
    }
    lastAutoScrollTime.current = now;

    if (currentParagraphIndex === -1) return;

    debouncedScrollToParagraph(currentParagraphIndex);
  }, [
    currentTime,
    currentParagraphIndex,
    isEditMode,
    debouncedScrollToParagraph,
  ]);

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Typography variant="h6">Transcript</Typography>
        <VerticalDotsMenu
          onExportSubtitle={handleExportSubtitle}
          onEditSubtitle={handleEditSubtitle}
        />
      </Box>

      {isEditMode && (
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexShrink: 0 }}>
          <Button variant="outlined" onClick={handleCancelEdit} size="small">
            Exit Edit Mode
          </Button>
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          pr: 1,
          overflowY: "auto",
          overflowX: "hidden",
          mt: 2,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {currentParagraphs.map((paragraph, index) => (
          <ParagraphItem
            key={paragraph.id}
            paragraph={paragraph}
            index={index}
            isEditMode={isEditMode}
            currentTime={currentTime}
            onParagraphClick={onParagraphClick}
            onTextChange={handleParagraphTextChange}
            setRef={setParagraphRef}
            isEditing={editingParagraphIndex === index}
            onStartEdit={setEditingParagraphIndex}
            onStopEdit={() => setEditingParagraphIndex(null)}
          />
        ))}
      </Box>
    </Box>
  );
}
